import { Component, OnInit, Input, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { Product, Negotiation, NegotiationEvent, Price } from '@vestiaire/api-sdk-angular';
import { LocaleService } from '@app/core/i18n';
import { MmaoService } from '@app/shared/components/mmao/services/mmao.service';
import { ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import * as MmaoActions from '@app/store/actions/mmao.action';
import { UserService } from '@app/core/user';
import { MmaoData } from '@app/analytics/models/types';
import { MatDialog } from '@angular/material/dialog';
import { PriceDropService } from '@app/shared/services/price-drop/priceDrop.service';
@Component({
  selector: 'vc-mmao',
  templateUrl: './mmao.component.html',
  styleUrls: ['./mmao.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MmaoComponent implements OnInit {
  /**
   * Product to be negotiated.
   */
  @Input()
  product: Product;

  /**
   * Current negotiation.
   */
  @Input()
  negotiation: Negotiation | null;

  /**
   * If nego initialized by seller, buyerId is comment author id
   */
  @Input()
  buyerId: string | null;

  @Input()
  commentId: string | null;

  @Output()
  submitting = new EventEmitter<boolean>();

  /**
   * Current user's role in negotiation process.
   */
  userRole: NegotiationEvent.CreatedByEnum = NegotiationEvent.CreatedByEnum.Buyer;

  minOffer: number;
  maxOffer: number;

  currentAction: Negotiation.NextActionsEnum | null | 'new';

  trackingData: MmaoData;
  errorMessage: string;

  constructor(
    private localeService: LocaleService,
    private mmaoService: MmaoService,
    private userService: UserService,
    private store: Store<IAppState>,
    private modal: MatDialog,
    private priceDropService: PriceDropService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (!this.product) {
      throw new Error('No selected product for MMAO process');
    }

    this.userService.getAuthenticatedUser().subscribe(
      user => {
        if (!user) {
          throw new Error('You are not allowed to use this feature when not signed in.');
        }
        if (this.product && this.product.seller) {
          this.userRole =
            user.id === this.product.seller.id
              ? NegotiationEvent.CreatedByEnum.Seller
              : NegotiationEvent.CreatedByEnum.Buyer;
        } else if (this.negotiation) {
          this.userRole =
            user.id === this.negotiation.seller.id
              ? NegotiationEvent.CreatedByEnum.Seller
              : NegotiationEvent.CreatedByEnum.Buyer;
        }
      },
      err => {
        throw new Error('Cannot find authenticated user');
      }
    );

    if (!this.negotiation) {
      this.minOffer = this.mmaoService.getMinOffer(this.initialPrice);
      this.maxOffer = this.mmaoService.getMaxOffer(this.initialPrice);
      this.currentAction = 'new';
      this.changeDetector.markForCheck();
    } else {
      this.minOffer = Math.round(this.negotiation.minOffer.cents / 100);
      this.maxOffer = Math.round(this.negotiation.maxOffer.cents / 100);
    }

    /**
     * Init tracking data
     */
    this.trackingData = { product: this.product, userRole: this.userRole };
  }

  get currency(): string {
    if (this.negotiation) {
      return this.negotiation.price.currency as string;
    }

    if (this.product && this.product.depositPrice && this.userRole === NegotiationEvent.CreatedByEnum.Seller) {
      return this.product.depositPrice.currency as string;
    }

    return this.localeService.getCurrency();
  }

  get initialPrice(): Price {
    if (this.negotiation) {
      return this.negotiation.initialPrice;
    }

    if (this.product.depositPrice && this.userRole === NegotiationEvent.CreatedByEnum.Seller) {
      return this.product.depositPrice;
    }

    return this.product.price;
  }

  get isLastOffer(): boolean {
    if (!this.negotiation || !this.negotiation.nextActions || !this.negotiation.nextActions.includes('offer')) {
      return false;
    }

    if (this.userRole === NegotiationEvent.CreatedByEnum.Buyer) {
      return this.negotiation.buyerRemainingOffers === 1;
    }

    if (this.userRole === NegotiationEvent.CreatedByEnum.Seller) {
      return this.negotiation.sellerRemainingOffers === 1;
    }

    return false;
  }

  get nextActions(): string[] {
    if (this.negotiation && this.negotiation.nextActions) {
      return this.negotiation.nextActions;
    }

    return [];
  }

  closePopin() {
    this.modal.closeAll();
  }

  selectAction(action: Negotiation.NextActionsEnum): void {
    this.currentAction = action;
  }

  clearAction(): void {
    this.currentAction = null;
  }

  submitOffer(price: number): void {
    this.submitting.emit(true);
    this.store.dispatch({ type: MmaoActions.MMAO_SUBMIT, payload: this.trackingData });

    if (this.currentAction === 'new') {
      this.createNegotiation(price); // New negotiation
    } else if (this.currentAction === 'offer') {
      this.addOffer(price);
    }
  }

  private createNegotiation(price: number): void {
    this.errorMessage = '';

    this.mmaoService.createNegotiation(this.product, price, this.buyerId).subscribe(
      res => {
        this.submitting.emit(false);
        this.update(res);
        this.store.dispatch({ type: MmaoActions.MMAO_MAKE_OFFER_SUCCESS, payload: this.trackingData });
        this.priceDropService.addPriceDropToLocalForage(this.product.id);
      },
      err => {
        this.submitting.emit(false);
        this.errorMessage = err.error.errors.detail;
        this.changeDetector.markForCheck();
      }
    );
  }

  private addOffer(price: number): void {
    this.errorMessage = '';

    this.mmaoService.sendOffer(this.negotiation, price).subscribe(
      res => {
        this.submitting.emit(false);
        this.update(res);
        // TODO :  Uncomment this code when tracking is activated for seller
        // this.store.dispatch({ type: MmaoActions.MMAO_MAKE_OFFER_SUCCESS, payload: this.trackingData });
      },
      err => {
        this.submitting.emit(false);
        this.errorMessage = err.error.errors.detail;
        this.changeDetector.markForCheck();
      }
    );
  }

  acceptOffer(): void {
    this.errorMessage = '';

    this.mmaoService.acceptOffer(this.negotiation).subscribe(
      res => {
        this.update(res);
        this.store.dispatch({ type: MmaoActions.MMAO_ACCEPT, payload: this.trackingData });
      },
      err => {
        this.errorMessage = err.error.errors.detail;
        this.changeDetector.markForCheck();
      }
    );
  }

  rejectOffer(): void {
    this.submitting.emit(true);
    this.errorMessage = '';

    this.mmaoService.rejectOffer(this.negotiation).subscribe(
      res => {
        this.submitting.emit(false);
        this.update(res);
        this.store.dispatch({ type: MmaoActions.MMAO_REJECT, payload: this.trackingData });
      },
      err => {
        this.submitting.emit(false);
        this.errorMessage = err.error.errors.detail;
        this.changeDetector.markForCheck();
      }
    );
  }

  private update(updatedNegotiation: Negotiation): void {
    this.negotiation = updatedNegotiation;
    this.changeDetector.markForCheck();

    this.store.dispatch({
      type: MmaoActions.ADD_MMAO,
      payload: { negotiation: updatedNegotiation, commentId: this.commentId }
    });

    this.clearAction();
  }
}
